// Tracked-security visibility panel for the dashboard.
//
// Surfaces, for the monitored market target:
//   1. the current drawdown from its all-time high (the headline number),
//   2. a margin-risk severity indicator (DEFCON-style, driven by how close deployed
//      margin is to the ladder's permitted ceiling, NOT raw drawdown depth), and
//   3. the alert ladder as a stepped visualization with a depth gradient, showing which
//      tier we're on, what each tier permits, and what the next drop would unlock.
//
// All tier/severity data is computed server-side (GET /state -> margin_headroom) so this
// view is consistent with alerts and any other consumer.

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import type {
  DrawdownState,
  MarginSeverity,
  MarketTarget,
  TierStatus,
} from "../types";
import { quoteTime } from "../lib/quoteFormat";
import { Card, CardContent } from "@/components/ui/card";

function percent(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  return `${value.toFixed(2)}%`;
}

// Severity -> presentation. Drives the headline chip color and label. "critical" is the
// only state that means "act now" (at or over the permitted margin).
const SEVERITY_META: Record<
  MarginSeverity,
  { label: string; compactLabel: string; chip: string; dot: string }
> = {
  none: {
    label: "No margin deployed",
    compactLabel: "No margin",
    chip: "bg-muted text-muted-foreground",
    dot: "bg-muted-foreground",
  },
  low: {
    label: "Low margin risk",
    compactLabel: "Low risk",
    chip: "bg-success/15 text-success",
    dot: "bg-success",
  },
  moderate: {
    label: "Moderate margin risk",
    compactLabel: "Moderate",
    chip: "bg-warning/15 text-warning",
    dot: "bg-warning",
  },
  high: {
    label: "High margin risk",
    compactLabel: "High risk",
    chip: "bg-destructive/15 text-destructive",
    dot: "bg-destructive",
  },
  critical: {
    label: "Over the permitted limit",
    compactLabel: "Over limit",
    chip: "bg-destructive text-destructive-foreground",
    dot: "bg-destructive-foreground",
  },
};

// Depth gradient across ladder rungs: shallow tiers green, deepening through amber to red,
// like a DEFCON scale. Keyed off the rung's position in the ladder so it reads as
// "how severe would it be to be down this far", independent of current margin usage.
function tierDepthColor(index: number, count: number): string {
  if (count <= 1) return "bg-success";
  const t = index / (count - 1); // 0 (shallow) .. 1 (deepest)
  if (t < 0.25) return "bg-success";
  if (t < 0.5) return "bg-warning";
  if (t < 0.75) return "bg-orange-500";
  return "bg-destructive";
}

function TierRow({
  tier,
  count,
}: {
  tier: TierStatus;
  count: number;
}) {
  const depth = tierDepthColor(tier.index, count);
  return (
    <li
      className={
        "flex items-center gap-3 rounded-md px-2 py-1.5 text-sm " +
        (tier.current ? "bg-card ring-1 ring-primary/50" : "")
      }
      data-testid={tier.current ? "current-tier" : undefined}
    >
      {/* Depth swatch; dimmed until the tier is reached. */}
      <span
        className={
          "size-2.5 shrink-0 rounded-full " +
          depth +
          (tier.reached ? "" : " opacity-30")
        }
        aria-hidden
      />
      <span
        className={
          "w-14 shrink-0 tabular-nums " +
          (tier.reached ? "font-medium text-foreground" : "text-muted-foreground")
        }
      >
        -{tier.drawdown_percent.toFixed(0)}%
      </span>
      <span
        className={
          "min-w-0 flex-1 truncate " +
          (tier.reached ? "text-foreground" : "text-muted-foreground")
        }
      >
        {tier.label}
        {tier.conditional && (
          <span className="ml-1 text-[10px] uppercase tracking-wide text-warning">
            conditional
          </span>
        )}
      </span>
      <span className="shrink-0 tabular-nums text-muted-foreground">
        permits {tier.maximum_margin_percent.toFixed(1)}%
      </span>
      {tier.current && (
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-primary">
          you are here
        </span>
      )}
    </li>
  );
}

export function TrackedSecurityPanel({
  target,
  state,
  showMarginUsage = true,
}: {
  target: MarketTarget;
  state: DrawdownState;
  showMarginUsage?: boolean;
}) {
  const hr = state.margin_headroom;
  const hasIntradayPrice = state.intraday_price != null;
  const drawdown = Math.abs(
    state.intraday_drawdown_percent ?? state.current_drawdown_percent,
  );
  const allTimeHigh = state.intraday_all_time_high ?? state.all_time_high;
  const displayedPrice = state.intraday_price ?? state.latest_close;
  const severity: MarginSeverity = hr?.severity ?? "none";
  const sev = SEVERITY_META[severity];
  // Price and drawdown already appear in the starred watchlist row directly below this
  // card. Keep only the unique policy status visible by default; expand for reference
  // prices, margin math, and the full ladder.
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <Card className="gap-0 py-0">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left sm:gap-3"
        onClick={() => setDetailsOpen((value) => !value)}
        aria-expanded={detailsOpen}
        data-testid="ladder-toggle"
      >
        {detailsOpen ? (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="shrink-0 font-semibold">{target.symbol} policy</span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {hr && hr.tier_count > 0 ? (
            <>
              Tier{" "}
              <span className="font-medium text-foreground">
                {hr.tier_index} of {hr.tier_count}
              </span>
              {` · ${hr.permitted_margin_percent.toFixed(1)}% permitted`}
              {hr.next_tier && ` · next at -${hr.next_tier.drawdown_percent.toFixed(0)}%`}
            </>
          ) : (
            "Policy details"
          )}
        </span>
        <span
          className={
            "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium " +
            sev.chip
          }
          data-testid="severity-chip"
          title={sev.label}
        >
          <span className={"size-1.5 rounded-full " + sev.dot} aria-hidden />
          {sev.compactLabel}
        </span>
      </button>

      {detailsOpen && (
        <CardContent className="space-y-3 border-t p-3">
          <div className="grid grid-cols-3 gap-px overflow-hidden rounded-lg bg-border">
            <Figure
              label="Drawdown from high"
              value={`-${drawdown.toFixed(2)}%`}
              emphasis={drawdown > 0}
            />
            <Figure label="All-time high" value={money(allTimeHigh)} />
            <Figure
              label={hasIntradayPrice ? "Latest price" : "Latest close"}
              value={money(displayedPrice)}
              hint={
                hasIntradayPrice && state.intraday_price_at
                  ? quoteTime(state.intraday_price_at)
                  : undefined
              }
            />
          </div>

          {showMarginUsage && hr && hr.current_ltv_percent != null && (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {percent(hr.current_ltv_percent)}
              </span>{" "}
              deployed of{" "}
              <span className="font-medium text-foreground">
                {percent(hr.permitted_margin_percent)}
              </span>{" "}
              permitted
              {hr.headroom_percent != null && (
                <>
                  {" "}·{" "}
                  <span
                    className={
                      "font-medium " +
                      (hr.headroom_percent < 0 ? "text-destructive" : "text-foreground")
                    }
                  >
                    {percent(hr.headroom_percent)}
                  </span>{" "}
                  headroom
                </>
              )}
            </p>
          )}

          {hr && hr.ladder.length > 0 && (
            <ul className="space-y-0.5">
              {hr.ladder.map((tier) => (
                <TierRow key={tier.index} tier={tier} count={hr.tier_count} />
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function money(value: number | undefined | null, currency = "USD"): string {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}

/** A compact figure cell inside the tracked-security headline grid. */
function Figure({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-0 bg-card px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          "truncate text-base font-semibold tabular-nums " +
          (emphasis ? "text-destructive" : "")
        }
      >
        {value}
      </div>
      {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
